import { ChangeDetectionStrategy, Component } from '@angular/core';
import * as cee from '../../Common/CeeEnvisionWebComponents';
import { C3Service } from '../../c3.service';
import { FeatureEditorChild } from '../feature-editor/feature-editor.component';

@Component({
    selector: 'app-particle-trace-editor',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './particle-trace-editor.component.html',
    styleUrls: ['./particle-trace-editor.component.css'],
    standalone: false
})
export class ParticleTraceEditorComponent extends FeatureEditorChild {
  private _c3Service: C3Service;
  private _ptg!: cee.ug.ParticleTraceGroup;
  private _seedCenter!: cee.Vec3Like;
  private _seedNormal!: cee.Vec3Like;
  private _numU = 10;
  private _numV = 10;
  private _spacing = 0.0003;
  private _pickActive = false;
  private _visualizationStyles = [
    { id: cee.ug.ParticleTraceVisualizationStyle.CYLINDERS, name: 'Cylinders' },
    { id: cee.ug.ParticleTraceVisualizationStyle.SPHERES, name: 'Spheres' },
    { id: cee.ug.ParticleTraceVisualizationStyle.COMETS, name: 'Comets' },
  ];

  public get visualizationStyles() {
    return this._visualizationStyles;
  }
  public get scalarResultsWithNone() {
    return this._c3Service.scalarResultsWithNone;
  }
  public get vectorResults() {
    return this._c3Service.vectorResults;
  }
  public get hasParticleTrace() {
    return this._ptg !== undefined;
  }
  public get vectorResultId() {
    return this._ptg.vectorResultId;
  }
  public get mapScalarResultId() {
    return this._ptg.mapScalarResultId;
  }
  public get visualizationStyle() {
    return this._ptg.visualizationStyle;
  }
  public get color() {
    return this._ptg.color;
  }
  public get radius() {
    return this._ptg.radius;
  }
  public get pulseCount() {
    return this._ptg.pointSamplingPulseCount;
  }
  public get relativeCometLength() {
    return this._ptg.relativeCometLength;
  }
  public get traceBothDirections() {
    return this._ptg.traceBothDirections;
  }
  public get numU() {
    return this._numU;
  }
  public get numV() {
    return this._numV;
  }
  public get spacing() {
    return this._spacing;
  }
  public get pickActive() {
    return this._pickActive;
  }

  public get isSpheres() {
    return this.visualizationStyle === cee.ug.ParticleTraceVisualizationStyle.SPHERES;
  }
  public get isComets() {
    return this.visualizationStyle === cee.ug.ParticleTraceVisualizationStyle.COMETS;
  }
  public get pickButtonClass() {
    return this._pickActive ? 'active' : '';
  }

  public constructor(c3Service: C3Service) {
    super();
    this._c3Service = c3Service;
  }

  public getParticleTraces = () => {
    return this._c3Service._model['0']
      .getParticleTraceGroupArray()
      .map((cp: { id: 0 }) => ({ id: cp.id, name: 'Particle trace ' + cp.id }));
  };

  public addParticleTrace = () => {
    const ptg = this._c3Service._model['0'].addParticleTraceGroup();
    const vectorResults = this._c3Service._model['0'].modelDirectory.vectorResultArray;

    ptg.radius = 0.00005;
    if (vectorResults.length > 0) {
      ptg.vectorResultId = vectorResults[0].id;
    }

    return ptg.id;
  };

  public deleteParticleTrace = (id: number) => {
    this._c3Service._model['0'].deleteParticleTraceGroupById(id);
  };

  public particleTraceIdChanged(id: number) {
    this._ptg = id !== undefined ? this._c3Service._model['0'].getParticleTraceGroupById(id) : undefined;
  }

  public vectorResultIdChanged(value: string) {
    this._ptg.vectorResultId = parseInt(value);
  }

  public mapScalarResultIdChanged(value: string) {
    this._ptg.mapScalarResultId = parseInt(value);
  }

  public visualizationStyleChanged(value: string) {
    this._ptg.visualizationStyle = parseInt(value);
  }

  public colorChanged(value: cee.Color3Like) {
    this._ptg.color = value;
  }

  public radiusChanged(value: number) {
    this._ptg.radius = value;
  }

  public pulseCountChanged(value: number) {
    this._ptg.pointSamplingPulseCount = value;
  }

  public relativeCometLengthChanged(value: number) {
    this._ptg.relativeCometLength = value;
  }

  public traceBothDirectionsChanged(value: boolean) {
    this._ptg.traceBothDirections = value;
  }

  public numUChanged(value: number) {
    this._numU = value;
    this.setSeedPoints();
  }

  public numVChanged(value: number) {
    this._numV = value;
    this.setSeedPoints();
  }

  public spacingChanged(value: number) {
    this._spacing = value;
    this.setSeedPoints();
  }

  public togglePick() {
    this._pickActive = !this._pickActive;

    if (!this._pickActive) {
      this._c3Service.removePick();
    } else {
      this._c3Service.performPick((hitItem) => {
        if (hitItem.anythingHit) {
          this._seedCenter = hitItem.intersectionPoint;
          this._seedNormal = hitItem.intersectionPointNormal;
          this._pickActive = false;
          this.setSeedPoints();
        }
      });
    }
  }

  private setSeedPoints() {
    if (this._seedCenter) {
      this._ptg.seedPointArray = cee.ug.ParticleTraceUtils.computeGridPoints(
        this._seedCenter,
        this._seedNormal,
        this._numU,
        this._numV,
        this._spacing
      );
    }
  }
}
